// Lib
import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, FormikProps, Field } from 'formik';
import { Grid, Option } from '@inplayer-org/inplayer-ui';

// Actions
import { loadJWPlayers } from 'ducks/merchantDucks/items/actions';

// utils
import JSONparse from 'utils/json-parse-safe';

// selectors
import { getJwPlayers } from 'ducks/merchantDucks/items/selectors';

// Components
import Input from 'components/Fields/Input';
import Loader from 'components/Loader';
import Select from 'components/Fields/Select';
import ErrorMessage from 'components/ErrorMessage';
import { FullHeightForm, ContentContainer } from 'components/SharedStyledElements';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';

// hooks
import useMobileURL from './hooks/useMobileURL';

const { Container, Cell } = Grid;

interface OwnProps {
  rawContent: string;
  onSubmit: (contentString: string, externalAssetId: string) => void;
  formId: string;
  isReadOnly: boolean;
  closePanel: () => void;
}

type Props = OwnProps;

interface FormValues {
  type: string;
  player_id: string;
  video_id: string;
  drm_policy_id: string;
}

const defaultValues: FormValues = {
  type: 'vod',
  player_id: '',
  video_id: '',
  drm_policy_id: '',
};

const typeOptions: Array<Option> = [
  { displayName: 'VOD', value: 'vod', tag: 'vod' },
  { displayName: 'Live stream', value: 'livestream', tag: 'livestream' },
  { displayName: 'Instant live', value: 'instant_live', tag: 'instant_live' },
  { displayName: 'Broadcast live', value: 'broadcast_live', tag: 'broadcast_live' },
];

export const JWPlayerContent = ({ rawContent, onSubmit, formId, isReadOnly, closePanel }: Props) => {
  const dispatch = useDispatch();

  const { players, isFetching } = useSelector(getJwPlayers);

  const [{ isFetching: isFetchingMobileUrl }, getMobileUrl] = useMobileURL();

  const initialValues: FormValues = {
    ...defaultValues,
    ...JSONparse(rawContent).value,
  };

  useEffect(() => {
    if (!isFetching) {
      dispatch(loadJWPlayers());
    }
  }, []);

  const defaultOption = { displayName: 'Select video player', disabled: true };
  const jwVideos = players.map(({ key, name }: any) => ({
    value: key,
    displayName: name,
    tag: name,
  }));

  const handleRetrieveMobileURL = useCallback(
    (videoId: string, setFieldValue: (field: string, value: any) => void) => async () => {
      const mobileUrl = await getMobileUrl(videoId);

      setFieldValue('mobile_url', mobileUrl);
    },
    [getMobileUrl]
  );

  const handleSubmit = useCallback(
    (data: FormValues) => {
      const contentString = JSON.stringify(data);
      const { video_id: externalAssetId } = data;

      onSubmit(contentString, externalAssetId);
    },
    [onSubmit]
  );

  if (isFetching) {
    return <Loader />;
  }

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {({ values: { player_id, video_id, type }, setFieldValue }: FormikProps<FormValues>) => {
        if (isFetchingMobileUrl) {
          return <Loader />;
        }

        let videoTypeLabel;
        switch (type) {
          case 'instant_live':
            videoTypeLabel = 'Channel ID';
            break;
          case 'broadcast_live':
            videoTypeLabel = 'Stream ID';
            break;
          default:
            videoTypeLabel = 'Video ID';
        }

        return (
          <FullHeightForm id={formId}>
            <FormAccordionHelper initialValues={initialValues} index={SectionType.Content} closePanel={closePanel} />
            <ContentContainer withPadding>
              <Container columns={1} rowGap="1rem">
                <Cell>
                  <Field
                    tag="dropdown_type"
                    name="type"
                    label="Type"
                    value={type}
                    fullWidth
                    options={typeOptions}
                    component={Select}
                  />
                </Cell>
                <Cell>
                  <Field
                    tag="text_input_video_id"
                    name="video_id"
                    label={videoTypeLabel}
                    component={Input}
                    disabled={isReadOnly}
                    onBlur={handleRetrieveMobileURL(video_id, setFieldValue)}
                  />
                </Cell>
                <Cell>
                  <Field
                    tag="text_input_mobile_url"
                    name="mobile_url"
                    label="Mobile URL"
                    component={Input}
                    disabled={isReadOnly}
                  />
                </Cell>
                {!players.length ? (
                  <ErrorMessage>Please connect your JW Player OVP keys</ErrorMessage>
                ) : (
                  <Cell>
                    <Field
                      name="player_id"
                      label="Video Player"
                      value={player_id}
                      fullWidth
                      options={jwVideos}
                      defaultOption={defaultOption}
                      component={Select}
                      disabled={isReadOnly}
                    />
                  </Cell>
                )}
                <Cell>
                  <Field
                    tag="text_input_drm"
                    name="drm_policy_id"
                    label="Drm Policy Id"
                    component={Input}
                    disabled={isReadOnly}
                  />
                </Cell>
              </Container>
            </ContentContainer>
          </FullHeightForm>
        );
      }}
    </Formik>
  );
};

export default JWPlayerContent;
